import axios from 'axios';
function defaultShouldNotify() {
    return true;
}
function defaultPostProcess() { }
let notify;
let shouldNotify = defaultShouldNotify;
let postProcess = defaultPostProcess;
/**
 * Wires this module up as Vue's global error handler. Call once during app setup — in main.js,
 * after `const app = createApp(...)` and before `app.mount(...)`:
 *
 *   import { setupGlobalErrorHandler } from '@/lib/errors'
 *   const { errorToast } = useGlobalUtils()
 *   setupGlobalErrorHandler(app, {
 *     notify: ({ message }) => errorToast(message),
 *     postProcess: (error) => { if (error.statusCode === 401) router.push('/login') },
 *   })
 *
 * `shouldNotify`/`postProcess` are both optional — omit either to keep its default (see
 * SetupGlobalErrorHandlerOptions above for what each one does).
 */
export function setupGlobalErrorHandler(app, options) {
    notify = options.notify;
    shouldNotify = options.shouldNotify ?? defaultShouldNotify;
    postProcess = options.postProcess ?? defaultPostProcess;
    app.config.errorHandler = (err) => handleError(err);
}
/**
 * Generic error handler — not auth-specific, meant for any caught/rejected error app-wide.
 *
 * This app currently produces TWO different backend error shapes:
 *   - OLD: ResponseError (bcc.web.api\Models\Error.cs, via IError.ToError()) — flat
 *          { message, isInputError, meta }, used by most controllers today.
 *   - NEW: ApiExceptionResult (bcc.web.api\Middleware\ApiExceptionResult.cs), used by the global
 *          exception middleware — nested { status, statusCode, error: { code, message, details,
 *          timestamp, path, suggestion }, requestId, documentationUrl }.
 * normalizeError() understands both without requiring the rest of the app to migrate first.
 *
 * Predecessor: ui\src\helpers\globalUtils.js's `handleApiErr(error, nav, msgPassThrough, ignoreToast)`
 * (~line 216) is the existing app-wide error handler this is meant to eventually replace. Today it:
 *   - shows a hardcoded generic toast for any 500, without reading the actual error message
 *   - for any 401: signs the user out, redirects to /login, and shows "Your session has expired" —
 *     unconditionally, even for a login-time credential failure with no session to lose (this is
 *     exactly why AuthController now returns 400, never 401, for wrong-password/invalid-2FA/etc.)
 *   - otherwise reads the OLD (ResponseError) shape only — `data.message` / `data.isInputError` /
 *     `data.meta` — and has no knowledge of the newer ApiExceptionResult shape at all
 * Keep both handlers working side by side until call sites are migrated deliberately, not by
 * changing what handleApiErr itself does.
 */
const UNKNOWN_ERROR = {
    statusCode: 500,
    messageCode: undefined,
    message: 'An unexpected error occurred.',
    meta: undefined,
    isInputError: false,
    zoneAwareStack: undefined,
    originalStack: undefined,
};
/**
 * Each fromX below returns a complete NormalizedError if `input` matches its shape, or undefined if
 * it doesn't. normalizeError tries them in order and takes the first match — exactly one of these
 * shapes is ever true for a given input, so there's no merging to do: once a shape matches, every
 * field for that shape lives at one specific, known location.
 */
/** A plain string was thrown/passed — the only thing extractable from it is the message. */
function fromErrorString(input) {
    if (typeof input !== 'string')
        return undefined;
    return { ...UNKNOWN_ERROR, message: input };
}
/**
 * A wrapped promise rejection (e.g. zone.js-style wrappers — handled defensively even though this
 * app doesn't use zone.js today, in case a future dependency introduces one).
 */
function fromRejectionPromise(input) {
    const wrapper = input;
    if (!wrapper?.promise || !wrapper?.rejection)
        return undefined;
    return {
        ...UNKNOWN_ERROR,
        message: wrapper.rejection.message ?? UNKNOWN_ERROR.message,
        zoneAwareStack: wrapper.zoneAwareStack,
        originalStack: wrapper.originalStack,
    };
}
/**
 * Old ResponseError-shaped body (bcc.web.api\Models\Error.cs, via IError.ToError()). Discriminated on
 * body.message — the one field this shape always provides. Takes the already-unwrapped axios body,
 * not input — normalizeError below unwraps the axios envelope exactly once.
 */
function fromIErrorResponse(body, status) {
    const old = body;
    if (!old?.message)
        return undefined;
    return {
        ...UNKNOWN_ERROR,
        statusCode: status ?? UNKNOWN_ERROR.statusCode,
        message: old.message,
        isInputError: old.isInputError ?? false,
        meta: old.meta,
    };
}
/**
 * New ApiExceptionResult-shaped nested error (bcc.web.api\Middleware\ApiExceptionResult.cs).
 * Discriminated on error.message — the one field this shape always provides. Takes the
 * already-unwrapped axios body, not input, same as fromIErrorResponse above.
 */
function fromApiExceptionResult(body, status) {
    const data = body;
    const nested = data?.error;
    if (!nested?.message)
        return undefined;
    return {
        ...UNKNOWN_ERROR,
        statusCode: data?.statusCode ?? status ?? UNKNOWN_ERROR.statusCode,
        messageCode: nested.code,
        message: nested.message,
    };
}
/**
 * Normalizes any thrown/rejected value into one consistent shape. First unwraps the axios envelope
 * exactly once — via axios.isAxiosError, its own runtime discriminator, not a structural guess — then
 * tries each fromX above in turn and takes the first match. Nothing recognized — including an axios
 * error whose body matches neither known shape — falls back to UNKNOWN_ERROR, keeping the real HTTP
 * status if axios gave us one.
 */
export function normalizeError(input) {
    const axiosError = axios.isAxiosError(input) ? input : undefined;
    const status = axiosError?.response?.status;
    const body = axiosError?.response?.data;
    return (fromErrorString(input) ??
        fromRejectionPromise(input) ??
        // special axios errors (when back-end returns concrete type)
        fromIErrorResponse(body, status) ??
        fromApiExceptionResult(body, status) ??
        // generic axios error message (for unknown/unsupported return types)
        {
            ...UNKNOWN_ERROR,
            statusCode: status ?? UNKNOWN_ERROR.statusCode,
            // No response at all (network down, timeout, CORS) means no backend message is ever coming —
            // axios's own message ("Network Error", "timeout of 5000ms exceeded") is the only diagnostic
            // info available here, so it's worth surfacing as the last resort.
            message: axiosError?.message ?? UNKNOWN_ERROR.message,
        });
}
/**
 * Maps a status code to a severity — shared by console logging and the notify payload's level. No
 * status code at all (a raw JS exception, a network failure with no response) defaults to 'error',
 * not 'info' — an unknown failure mode is a bug worth surfacing loudly, not a quiet one.
 */
function severityFor(statusCode) {
    const status = statusCode ?? 500;
    if (status >= 500)
        return 'error';
    if (status >= 400)
        return 'warn';
    return 'info';
}
const CONSOLE_BY_LEVEL = {
    error: console.error,
    warn: console.warn,
    info: console.info,
};
/** Logs a normalized error at a severity matched to its status code. */
function logError(error) {
    CONSOLE_BY_LEVEL[severityFor(error.statusCode)](error.message, error);
}
/**
 * Handles one error end to end: normalize, log, and (if a notifier has been registered via
 * setupGlobalErrorHandler) notify. This is the function handed to Vue's global error handler, and
 * is also fine to call directly from any catch block.
 */
export function handleError(err) {
    const error = normalizeError(err);
    logError(error);
    if (notify && shouldNotify(error)) {
        notify({ message: error.message, level: severityFor(error.statusCode), meta: error.meta });
    }
    postProcess(error);
    return error;
}
